import React, { Component } from 'react'
import {
  BadRequestError,
  SimpleReadingList,
  SimpleReadingListBatchValidateRes,
  SimpleReadingListBatchValidateResEntry,
  SimpleReadingListEntry,
  SimpleReadingLists
} from 'stylewhere/api'
import {
  AntennaButton,
  Box,
  Button,
  EntryReadingCounters,
  EntryReadingList,
  FullLoadingLayer,
  Page,
  Spacer,
  TagCounter
} from 'stylewhere/components'
import { SimpleReadingListExtensions } from 'stylewhere/extensions'
import { T, __ } from 'stylewhere/i18n'
import {
  OperationReadingProps,
  OperationReadingProvider,
  OperationReadingState,
  RemoteOperation,
  RfidReader,
  Router,
  SimpleReadingListOperationConfig
} from 'stylewhere/shared'
import {
  filterErrorEntries,
  filterIgnoredEntries,
  isModalError,
  showToast,
  showToastError
} from 'stylewhere/shared/utils'

interface State extends OperationReadingState {
  confirming: boolean
  barcodeReading: boolean
  entries: SimpleReadingListBatchValidateResEntry[]
  expectedEntries: SimpleReadingListBatchValidateResEntry[]
  simpleReadingList: SimpleReadingList
}

export default class SimpleReadingListReading<
  P extends OperationReadingProps<State>,
  S extends State
> extends Component<P, S> {
  antennaRef
  matchParams = Router.getMatchParams(this.props)
  locationState = Router.getLocationState<State>(this.props)
  simpleReadingList = this.props.location.state?.simpleReadingList
  operation = RemoteOperation.getOperationConfig<SimpleReadingListOperationConfig>(this.matchParams.opCode)
  formSchema = SimpleReadingListExtensions.formSchema(this.operation)
  isModal = false
  showQuantity = this.operation.targetEntity === 'products' || this.operation.targetEntity === 'NONE'
  initialEntries: SimpleReadingListEntry[] = []

  state: S = {
    entries: [],
    expectedEntries: [],
    loading: false,
    formData: this.locationState.formData ?? {},
    confirming: false,
    barcodeReading: false
  } as any

  constructor(props) {
    super(props)
    this.antennaRef = React.createRef()
  }

  async componentDidMount() {
    if (!this.simpleReadingList) {
      this.goBack()
      return
    }
    this.isModal = isModalError(this.operation)
    const barcodeReading: boolean = this.operation.readingMode === 'barcode'

    RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
    RfidReader.setAutomaticStopTime(this.operation.autostopAntennaTimeout)
    RfidReader.setOnTagReadCallback((tag) => this.onTagReceived(tag.epc))

    if (this.operation.hasChecklist) {
      this.setState({ loading: true, barcodeReading }, this.fetchEntries)
    } else {
      this.setState({ barcodeReading }, this.checkAutoStartAntenna)
    }
  }

  fetchEntries = async () => {
    try {
      this.initialEntries =
        this.simpleReadingList && this.simpleReadingList.id
          ? (await SimpleReadingLists.getEntries(this.simpleReadingList.id)).content
          : []
      const allEntriesGrouped = OperationReadingProvider.setDetectedQty([], this.initialEntries)

      this.setState({ expectedEntries: allEntriesGrouped, loading: false }, this.checkAutoStartAntenna)
    } catch (e) {
      this.setState({ expectedEntries: [], loading: false }, this.checkAutoStartAntenna)
      showToastError(__(T.misc.error), (e as any).message ?? __(T.error.search_error), true)
    }
  }

  checkAutoStartAntenna = () => {
    if (this.operation.autostartAntenna && this.antennaRef && this.antennaRef.current) {
      this.antennaRef.current.startReader()
    }
  }

  onTagReceived = async (tag: string) => {
    const decoded = await this.batchValidate(tag)
    this.handleEntries(decoded)
  }

  batchValidate = (epc: string) => {
    const batchValidate = {
      operationId: this.operation.id,
      simpleReadingListId: this.simpleReadingList.id,
      codes: [{ code: epc, quantity: 1 }]
    }
    return SimpleReadingLists.batchValidate(batchValidate)
  }

  handleEntries = async (decoded: SimpleReadingListBatchValidateRes) => {
    const { entries } = this.state
    const entityIds = Object.keys(decoded)
    const processedDecodedEntries: any[] = [] // saranno entity con diversi tipi
    entityIds.forEach((id) => {
      const entity = decoded[id]
      // if (!this.hasChecklist() && !entity.data) return // letto qualcosa che non è a sistema viene ignorato
      entity.data = entity.data || { code: id }

      if (!this.showQuantity) {
        const findEntry = entries.find((entry) => entry.data?.code === entity.data?.code)
        if (findEntry) return // non è possibile leggere due volte lo stesso code
      }

      OperationReadingProvider.checkEntryStatus(this.operation, entity)
      const processedEntry = SimpleReadingListExtensions.processEntry(this.operation, entity)
      if (processedEntry) processedDecodedEntries.push(processedEntry)
    })

    let countError = 0
    processedDecodedEntries.forEach((decodedEntry) => {
      if (
        decodedEntry.status === 'ignored' ||
        decodedEntry.status === 'ignoredWithReason' ||
        decodedEntry.status === 'error' ||
        decodedEntry.status === 'warning'
      ) {
        countError++
      }
      entries.push(decodedEntry)
    })

    await this.setState({ entries })
    if (this.operation.hasChecklist) this.calculateEntry(entries)
  }

  calculateEntry(entries: SimpleReadingListBatchValidateResEntry[]) {
    const groupedEntries = OperationReadingProvider.groupEntries(entries)
    const allEntriesGrouped = OperationReadingProvider.setDetectedQty(groupedEntries, this.initialEntries)
    this.setState({ expectedEntries: allEntriesGrouped })
  }

  onConfirm = async () => {
    const { entries } = this.state
    if (entries.filter(filterErrorEntries).length > 0) {
      showToastError(__(T.error.confirm_not_allowed_with_errors), __(T.error.error), true)
    } else {
      this.setState({ confirming: true }, this._confirm)
    }
  }

  _confirm = async () => {
    const { entries } = this.state
    try {
      if (this.antennaRef && this.antennaRef.current) {
        await this.antennaRef.current.stopReader()
      }

      const validEntries = entries.filter(filterIgnoredEntries)

      // update readings
      const updateReq = {
        operationId: this.operation.id,
        simpleReadingListId: this.simpleReadingList.id,
        updateAction: 'INSERT',
        codes: validEntries.map((e) => {
          return { code: e.data?.code }
        }) // TODO gestire items?
      }
      await SimpleReadingLists.updateReadings(updateReq)

      // confermiamo
      const confirmReq = {
        operationId: this.operation.id,
        simpleReadingListId: this.simpleReadingList.id
      }
      await SimpleReadingLists.confirm(confirmReq)
      this.setState({ confirming: false })
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.generic_success, { code: this.operation.description }),
        status: 'success'
      })
      this.goBack()
    } catch (err) {
      this.setState({ confirming: false })
      if (err instanceof BadRequestError) {
        showToastError(err, err.title || __(T.error.error), this.isModal)
      } else {
        showToastError(err, __(T.error.error), this.isModal)
      }
    }
  }

  goDashboard = () => {
    Router.navigate('/')
  }

  goBack = () => {
    if (this.formSchema.length || this.operation.showListOnStart) {
      Router.navigate('/simple-reading-list/:opCode', { opCode: this.operation.code })
    } else {
      this.goDashboard()
    }
  }

  onClear = () => {
    if (this.operation.hasChecklist) this.fetchEntries()
    RfidReader.clear()
    this.setState({ entries: [] })
  }

  showConfirmButton() {
    const { entries, confirming } = this.state
    if (entries.filter(filterIgnoredEntries).length > 0)
      return <Button loading={confirming} title={__(T.misc.confirm)} onClick={this.onConfirm} />
    return null
  }

  showClearButton() {
    const { entries, barcodeReading } = this.state
    if (entries.length > 0 && barcodeReading)
      return <Button style={{ marginTop: 10 }} variant="ignore" title={__(T.misc.clear)} onClick={this.onClear} />
    return null
  }

  getHeaderDetails = (schema) => {
    const { formData } = this.state
    return {
      details: {
        data: formData,
        formSchema: schema,
        operationId: this.operation.id
      }
    }
  }

  render() {
    const { entries, expectedEntries, loading, barcodeReading } = this.state
    return (
      <Page
        title={this.operation.description}
        onBackPress={() => this.goBack()}
        alertOnBackPress={this.operation.showAlertOnExit && entries.length > 0}
        loading={loading} // non implementato
        header={this.getHeaderDetails(this.formSchema)}
        userSettings={{ operation: this.operation, settings: this.operation.userSettings }}
        enableEmulation
        activeAntennaSettings
        styleSettingsButton={{ marginLeft: 0 }}
      >
        <Page.Sidebar>
          <Box flex style={{ overflowY: 'auto' }}>
            <TagCounter detected={entries.filter(filterIgnoredEntries).length} />
            <AntennaButton
              ref={this.antennaRef}
              mode={barcodeReading ? 'barcode' : 'antenna'}
              onTagReadCallback={(tag) => this.onTagReceived(tag.epc)}
              onClear={this.onClear}
              hideClear={entries.length === 0}
              barcodeModeOptions={{
                displayMode: 'inputReplacesAntennaButton'
              }}
            />
            <Spacer />
            <EntryReadingCounters operation={this.operation} entries={entries} />
          </Box>
          {this.showConfirmButton()}
          {this.showClearButton()}
        </Page.Sidebar>
        <Page.Content notBoxed>
          <EntryReadingList
            entries={this.operation.hasChecklist ? expectedEntries : entries}
            operation={this.operation}
            simpleReadingListId={this.simpleReadingList?.id}
          />
        </Page.Content>
        {loading && <FullLoadingLayer message={__(T.misc.loading)} />}
      </Page>
    )
  }
}
